import { useCallback, useMemo, useState } from "react";
import { useLanguage } from "../../../../../stores/LanguageStore";
import { useGridApiRef } from "@mui/x-data-grid-pro";
import { useListingLoader } from "../../../../../components/hooks/ListingHooks";
import SelectionDeleter from "../../../../../components/TableComponents/SelectionDeleter";
import ItemDeleter from "../../../../../components/TableComponents/ItemDeleter";
import { ActionIcon, Anchor, CopyButton, Group, Text, ThemeIcon, Tooltip } from "@mantine/core";
import dateColumn from "../../../../../layouts/Table/columns/dateColumn";
import { nameColumn } from "../../../../../layouts/Table/columns/nameColumn";
import MainTable from "../../../../../layouts/Table/MainTable";
import numberColumn from "../../../../../layouts/Table/columns/numberColumn";
import {
  deleteAIKey,
  editAIKey,
  getAIKeys,
} from "../../../../../api/ConfigsApi";
import { CommCode } from "../../../community/CommCode";
import { IconCheck, IconCopy, IconEdit, IconX } from "@tabler/icons-react";
import { Link } from "react-router-dom";
import AddExternalKey from "./AddExternalKey";
import NoItemsModal from "../../../../../components/TableComponents/NoItemsModal";

const ExternalAPIListing = ({ loading, setLoading }) => {
  const apiRef = useGridApiRef();
  const lang = useLanguage((s) => s.language);

  const [rows, setRows] = useState([]);

  const columns = useMemo(
    () => [
      numberColumn(),
      nameColumn({
        disableLink: true,
        editFunction: () => editAIKey,
        getDisabled: (row) => row.created_by === null,
        apiRef,
      }),
      {
        field: "api_key",
        headerName: lang.api_keys.columns.key,
        width: 150,
        renderCell: ({ value }) => {
          return (
            <Group wrap="nowrap" w={"100%"}>
              <Anchor c={"black"} truncate="end">
                {value}
              </Anchor>
              <CopyButton value={value}>
                {({ copied, copy }) => (
                  <Tooltip
                    label={copied ? lang.global.copied : `${lang.global.copy}`}
                    color={copied ? "green" : "black"}
                  >
                    <ActionIcon
                      size={"md"}
                      variant="light"
                      color={copied ? "green" : "primary"}
                      onClick={(e) => {
                        e.stopPropagation();
                        copy();
                      }}
                    >
                      {copied ? (
                        <IconCheck size={16} />
                      ) : (
                        <IconCopy size={16} />
                      )}
                    </ActionIcon>
                  </Tooltip>
                )}
              </CopyButton>
            </Group>
          );
        },
      },
      {
        field: "type",
        headerName: lang.api_keys.columns.type,
        width: 100,
        renderCell: ({ value }) => {
          return <Text tt={"uppercase"}> {value} </Text>;
        },
      },
      {
        field: "is_valid",
        headerName: lang.emails_account.columns.is_valid_column,
        width: 75,
        renderCell: ({ value }) => (
          <Tooltip
            label={
              value
                ? lang.emails_account.columns.is_valid
                : lang.emails_account.columns.not_valid
            }
          >
            <ThemeIcon variant="subtle" color={value ? "tertiary.8" : "red"}>
              {value ? <IconCheck /> : <IconX />}
            </ThemeIcon>
          </Tooltip>
        ),
      },
      dateColumn(),
    ],
    [lang, apiRef]
  );

  const actionsCell = useCallback(
    ({ row }) => {
      return (
        <Group wrap="nowrap" gap={"xs"}>
          {/* <Tooltip label={lang.global.edit}>
            <ActionIcon component={Link} to={`${row.id}`} variant="subtle">
              <IconEdit size={18} />
            </ActionIcon>
          </Tooltip> */}
          <ItemDeleter
            title_bold={`${row.name} #${row.id}`}
            handleDelete={deleteAIKey}
            deletedMessage={lang.integrations.delete_success}
            id={row.id}
            apiRef={apiRef}
          />
        </Group>
      );
    },
    [apiRef, lang.global.edit, lang.integrations.delete_success]
  );

  const toolbarActions = useCallback(
    ({ selectedIds }) => (
      <SelectionDeleter
        handleDelete={deleteAIKey}
        apiRef={apiRef}
        setTableLoading={setLoading}
        ids={selectedIds}
      />
    ),
    [apiRef]
  );

  useListingLoader({
    loading,
    setLoading,
    listGetter: getAIKeys,
    responseField: "external_api_keys_list",
    setRows,
  });

  return (
    <>
      <NoItemsModal
        rows={rows}
        loading={loading}
        text={
          <Text size="xl" mb={32}>
            {lang.settings.no_int}
          </Text>
        }
        button={<AddExternalKey />}
      />
      <MainTable
        apiRef={apiRef}
        columns={columns}
        rows={rows}
        loading={loading}
        setLoading={setLoading}
        id={"list-table"}
        whiteButtons
        toolbarActions={toolbarActions}
        actionsCell={actionsCell}
        actionsCellWidth={75}
      />
    </>
  );
};

export default ExternalAPIListing;
